import React, { type JSX } from 'react';
import {
  type ControllerProps,
  useFormContext,
  Controller,
} from 'react-hook-form';

/**
 * @name withHooksController
 * @description Wraps a given input with a Controller from react-hook-form
 */

export interface FormControllerProps {
  name: string;
  label?: string;
  controllerProps?: Partial<ControllerProps>;
  helperText?: React.ReactNode;
}

type InjectedPropsFromController = 'onChange' | 'value';

export function withFormController<P extends object>(
  Component: React.ComponentType<P>
): (
  props: Omit<P, InjectedPropsFromController> & FormControllerProps
) => JSX.Element {
  return ({ controllerProps, ...props }) => {
    const context = useFormContext();

    React.useEffect(() => {
      if (context == null && controllerProps?.control == null) {
        throw new Error(
          'withFromController must either be used within a FormProvider or provided a valid control prop'
        );
      }
    });

    return (
      <Controller
        {...controllerProps}
        control={controllerProps?.control ?? context.control}
        name={props.name}
        render={({ field, fieldState: { error, ...fieldState } }) => {
          return (
            <Component
              {...field}
              {...fieldState}
              error={error && error.message}
              errorMessage={error && error.message}
              helperText={error ? error.message : props.helperText}
              {...(props as P)}
            />
          );
        }}
      />
    );
  };
}
